import React from 'react';
import Formsy from '../../external/Formsy';
import PropTypes from 'prop-types';
import { ValidatingSelect, ValidatingInput, Message } from '../';
import { GATEWAY_TYPE, INDIVIDUAL_SOLE_PROPRIETOR, SSN, EIN } from '../../constants/PayoutConstants';
import Countries from '../../constants/Countries';
import PayoutSchema from '../../data/schema/PayoutSchema';
import TaxInfoSchema from '../../data/schema/TaxInfoSchema';
import PaypalAccount from './payoutInfo/PaypalAccount';
import BankAccount from './payoutInfo/BankAccount';
import TaxInfo from './taxInfo/TaxInfo';
import W9 from './w9/W9';
import ErrorModal from '../common/modal/ErrorModal';
import Modal from '../common/modal/Modal';

const formatSSNValue = value => {
    const extValue = value.replace(/-/g, '');
    const first = extValue.substr(0, 3);
    let second = extValue.substr(3, 2);
    if (second) {
        second = `-${second}`;
    }
    let third = extValue.substr(5, 4);
    if (third) {
        third = `-${third}`;
    }
    return `${first}${second}${third}`;
};

export default class PayoutFormBase extends React.Component {
    static propTypes = {
        hideTax: PropTypes.bool,
        save: PropTypes.func,
        get: PropTypes.func,
        setForm: PropTypes.func,
        hideModel: PropTypes.func,
        messages: PropTypes.object,
        payoutState: PropTypes.object,
        config: PropTypes.object,
        error: PropTypes.object,
        clearAlert: PropTypes.func,
        history: PropTypes.object
    };

    state = {
        isDocSignClicked: false // EGS-5061 fake re enable submit when click docSignLink
    };

    componentDidMount() {
        this.props.get();
    }

    handleSubmit = model => {
        const { payoutState, hideTax } = this.props;
        const { form } = payoutState;
        const { isDocSignClicked } = this.state;
        const payoutDisable = form.accountStatus && form.accountStatus === 'ACTIVE';
        const taxinfoDisable = form.status && form.status === 'ACTIVE';
        if (payoutDisable && (hideTax || (taxinfoDisable && isDocSignClicked))) {
            this.props.history.push('/competitive/success');
            return;
        }

        const { phoneNumber, ...formModel } = model;
        const phoneNo = phoneNumber ? { phoneNumber } : {}; // don't submit phone number if value is falsy

        const payload = {
            ...formModel,
            ...phoneNo,
            hideTax,
            accountStatus: form.accountStatus || null,
            status: form.status || null,
            foreignBank: formModel.foreignBank === 'true',
            foreignTax: formModel.foreignTax === 'true'
        };
        // if foreignTax is true and individual selected taxType will be ''
        if (!hideTax && !payload.taxType) {
            delete payload.taxType;
        }
        this.props.save(payload, this.props.history);
    };

    getFormValue = property => {
        const { form } = this.props.payoutState;
        if (typeof form[property] === 'boolean') return form[property].toString();
        if (property.includes('.')) {
            return form[property.split('.')[0]][property.split('.')[1]] || '';
        }
        return form[property] ? form[property] : '';
    };

    handleFormFieldChange = property => {
        const taxInfoSchema = new TaxInfoSchema(this.props.messages);
        const payoutSchema = new PayoutSchema(this.props.messages);

        return event => {
            const { form } = this.props.payoutState;
            let value = event.target.value || '';
            if (value === 'true') value = true;
            if (value === 'false') value = false;
            // SSN
            if (property === 'taxId' && form.taxType === 'SSN' && value) {
                value = formatSSNValue(value);
            }

            // foreignTax change
            if (property === taxInfoSchema.foreignTax.identifier) {
                // country
                form.address.country = value ? Countries.slice(1)[0].value : 'US';
                // taxType
                if (!value) {
                    const isIndividualSoleProprietor = form[taxInfoSchema.businessType.identifier] === INDIVIDUAL_SOLE_PROPRIETOR;
                    form[taxInfoSchema.taxType.identifier] = isIndividualSoleProprietor ? SSN : EIN;
                } else {
                    form[taxInfoSchema.taxType.identifier] = '';
                }
                form.taxId = '';
            }
            // businessType change
            if (property === taxInfoSchema.businessType.identifier) {
                const foreignTax = form[taxInfoSchema.foreignTax.identifier];
                const isIndividualSoleProprietor = value === INDIVIDUAL_SOLE_PROPRIETOR;

                if (!foreignTax) {
                    form[taxInfoSchema.taxType.identifier] = isIndividualSoleProprietor ? SSN : EIN;
                    if (isIndividualSoleProprietor) {
                        form.taxId = '';
                    }
                }
            }

            // taxtype change
            if (property === taxInfoSchema.taxType.identifier) {
                form.taxId = '';
            }

            // foreignBank change
            if (property === payoutSchema.foreignBank.identifier) {
                form.bankAccountNo = '';
            }

            if (property.includes('.')) {
                // TON-44235eSport - Cannot enter blank when user fill in tax info Address/BusinessName/City
                if (property === taxInfoSchema.address.identifier || property === taxInfoSchema.city.identifier) {
                    form[property.split('.')[0]][property.split('.')[1]] = value;
                } else {
                    form[property.split('.')[0]][property.split('.')[1]] = value.trim();
                }
            } else if (
                property === taxInfoSchema.businessName.identifier ||
                property === taxInfoSchema.nameOnTaxId.identifier ||
                property === payoutSchema.bankName.identifier ||
                property === payoutSchema.checkName.identifier) {
                form[property] = value;
            } else {
                form[property] = typeof value === 'string' ? value.trim() : value;
            }
            this.props.setForm({ form });
        };
    };

    handleDocSignClick = e => {
        this.setState({ isDocSignClicked: true });
    };

    ConfirmDialog = () => {
        const { alert } = this.props.payoutState;
        return (
            <Modal open={!!alert} onClose={this.props.clearAlert} title="epic.esport.error.modal.title">
                <Message code="epic.esport.success.save" />
            </Modal>
        );
    };

    AccountInfo = () => {
        const { form } = this.props.payoutState;
        const payoutDisable = form.accountStatus && form.accountStatus === 'ACTIVE';
        const payoutSchema = new PayoutSchema(this.props.messages);
        const { payPal } = payoutSchema;
        if (form.gatewayType === GATEWAY_TYPE.PAYPAL) {
            return (<PaypalAccount payPalSchema={payPal} onChange={this.handleFormFieldChange} getValue={property => this.getFormValue(property)} disabled={payoutDisable} />);
        }
        return (<BankAccount
            foreignBankValue={form.foreignBank}
            schema={payoutSchema}
            onChange={this.handleFormFieldChange}
            getValue={property => this.getFormValue(property)}
            disabled={payoutDisable}
        />);
    };

    PayoutForm = () => {
        const { config, hideTax } = this.props;
        const { form } = this.props.payoutState;
        const payoutDisable = form.accountStatus && form.accountStatus === 'ACTIVE';
        const taxinfoDisable = form.status && form.status === 'ACTIVE';
        const payoutSchema = new PayoutSchema(this.props.messages);
        const { gatewayType } = payoutSchema;
        const gatewayTypeOptions = [
            { value: GATEWAY_TYPE.PAYPAL, label: gatewayType.optionLabel.payPal },
            { value: GATEWAY_TYPE.WIRE_TRANSFER, label: gatewayType.optionLabel.bank }
        ];

        return (<Formsy onValidSubmit={this.handleSubmit}>
            <ValidatingInput type="hidden" name="id" value={form.id} />
            <div className="row">
                <ValidatingSelect
                    customClassNames="col-xs-12 col-md-8 col-lg-6 code-input"
                    name={gatewayType.identifier}
                    options={gatewayTypeOptions}
                    value={this.getFormValue(gatewayType.identifier)}
                    onChange={this.handleFormFieldChange(gatewayType.identifier)}
                    disabled={payoutDisable}
                />
            </div>
            <this.AccountInfo />

            {!hideTax && (<TaxInfo formValues={form} onChange={this.handleFormFieldChange} getValue={property => this.getFormValue(property)} disabled={taxinfoDisable} />)}

            {!hideTax && (<W9 onDocSignClick={this.handleDocSignClick} />)}

            <this.SubmitBtn />

            <div className="privacy-policy">
                <a href={config.privacyPolicyAddress} target="_blank"><Message code="epic.esport.label.privacyPolicy" /></a>
            </div>
        </Formsy>);
    };

    PageTitle = () => [
        <h2 key="title" className="payout-title">
            <Message code="epic.esport.label.payout_methods" />
        </h2>,
        <p key="desc">
            <Message code="epic.esport.label.payout_methods_info" />
        </p>
    ];

    render() {
        const { loading, error } = this.props.payoutState;

        return (
            <div className="payout-block">
                {loading && (
                    <div className="loading-wrapper">
                        <div className="bg-img" />
                    </div>
                )}
                <this.PageTitle />
                <this.PayoutForm />
                <ErrorModal showDialog={!!error} title="epic.esport.error.modal.title" message={error && error.errorCode} closeModal={this.props.hideModel} />
                <this.ConfirmDialog />
            </div>
        );
    }
}

